import React, { useContext, useState } from 'react';
import { Header } from '../../components/Header';
import {
  Box, Container, Typography, Paper, Button, Chip, LinearProgress,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
  Alert,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded';
import RmfDialog, { DialogActions as RmfDialogActions, DialogIcon, DialogText, DialogTitle as RmfDialogTitle, dialogButtonSx } from '../../components/Dialog/RmfDialog';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { myLoyaltyPoints, loyaltyRedemptionTiers, redeemLoyaltyPoints, loyaltyTierConfig, myBlackCardSubscription } from '../../apollo/server';
import UserContext from '../../context/User';
import useBlackCardProgram, { bcMoney } from '../../hooks/useBlackCardProgram';

const MY_BLACK_CARD = gql`${myBlackCardSubscription}`;

const MY_LOYALTY = gql`${myLoyaltyPoints}`;
const REDEMPTION_TIERS = gql`${loyaltyRedemptionTiers}`;
const REDEEM = gql`${redeemLoyaltyPoints}`;
const TIER_CONFIG = gql`${loyaltyTierConfig}`;

const TX_COLORS = {
  EARNED: '#16a34a',
  REDEEMED: '#dc2626',
  REVERSED: '#dc2626',
  EXPIRED: '#94a3b8',
  ADMIN_CREDIT: '#0284c7',
  ADMIN_DEBIT: '#dc2626',
};

const TIER_VISUALS = {
  SILVER: {
    gradient: 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 40%, #e2e8f0 100%)',
    glow: '0 8px 32px rgba(148,163,184,0.4)',
    labelColor: '#334155',
    accent: '#64748b',
    icon: '🥈',
    badge: { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
  },
  GOLD: {
    gradient: 'linear-gradient(135deg, #b45309 0%, #d97706 40%, #fbbf24 100%)',
    glow: '0 8px 32px rgba(251,191,36,0.45)',
    labelColor: '#fff',
    accent: '#fef3c7',
    icon: '🥇',
    badge: { bg: '#fef9c3', color: '#92400e', border: '#fde68a' },
  },
  PLATINUM: {
    gradient: 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 45%, #a78bfa 100%)',
    glow: '0 8px 32px rgba(124,58,237,0.45)',
    labelColor: '#fff',
    accent: '#ede9fe',
    icon: '💎',
    badge: { bg: '#f3e8ff', color: '#5b21b6', border: '#c4b5fd' },
  },
};

const DEFAULT_TIER_KEYS = ['SILVER', 'GOLD', 'PLATINUM'];

function getTierKey(tier) {
  if (!tier) return null;
  const up = (tier.tierKey || tier.tierName || '').toUpperCase();
  for (const k of DEFAULT_TIER_KEYS) {
    if (up.includes(k)) return k;
  }
  return null;
}

function TierBanner({ loyalty, tierConfigs }) {
  if (!tierConfigs || tierConfigs.length === 0) return null;

  const currentTierKey = loyalty?.tier || 'SILVER';
  const lifetimePoints = loyalty?.lifetimePoints ?? 0;

  // Sort by minLifetimePoints
  const sorted = [...tierConfigs].sort((a, b) => a.minLifetimePoints - b.minLifetimePoints);

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <EmojiEventsIcon sx={{ color: '#f59e0b' }} />
        <Typography variant="h6" fontWeight={700}>Membership Tiers</Typography>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: `repeat(${sorted.length}, 1fr)` },
        gap: 2,
      }}>
        {sorted.map((tier) => {
          const key = getTierKey(tier) || tier.tierKey?.toUpperCase();
          const vis = TIER_VISUALS[key] || TIER_VISUALS.SILVER;
          const isCurrentTier = currentTierKey === key;
          const isUnlocked = lifetimePoints >= tier.minLifetimePoints;
          const isLocked = !isUnlocked;

          // Progress within this tier
          const tierMax = tier.maxLifetimePoints ?? (tier.minLifetimePoints + 3000);
          const tierMin = tier.minLifetimePoints;
          const progressInTier = isLocked
            ? 0
            : Math.min(100, Math.round(((lifetimePoints - tierMin) / Math.max(1, tierMax - tierMin)) * 100));

          const ptsNeeded = isLocked ? tier.minLifetimePoints - lifetimePoints : 0;

          return (
            <Paper
              key={tier._id}
              elevation={isCurrentTier ? 6 : 1}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                border: isCurrentTier ? '2px solid transparent' : '1.5px solid #e2e8f0',
                background: isLocked ? '#f8fafc' : vis.gradient,
                boxShadow: isCurrentTier ? vis.glow : undefined,
                opacity: isLocked ? 0.7 : 1,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: isLocked ? undefined : 'translateY(-2px)', boxShadow: isLocked ? undefined : vis.glow },
              }}
            >
              {isCurrentTier && (
                <Box sx={{
                  position: 'absolute', top: 10, right: 10,
                  bgcolor: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(4px)',
                  borderRadius: 10, px: 1, py: 0.25,
                  display: 'flex', alignItems: 'center', gap: 0.5,
                }}>
                  <CheckCircleIcon sx={{ fontSize: 14, color: isLocked ? '#64748b' : '#fff' }} />
                  <Typography variant="caption" fontWeight={700} sx={{ color: isLocked ? '#64748b' : '#fff', fontSize: '0.65rem' }}>
                    CURRENT
                  </Typography>
                </Box>
              )}

              <Box sx={{ p: 2.5 }}>
                {/* Tier icon + name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography sx={{ fontSize: 28, lineHeight: 1 }}>
                    {isLocked ? '🔒' : vis.icon}
                  </Typography>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      sx={{ color: isLocked ? '#94a3b8' : vis.labelColor, lineHeight: 1.1 }}
                    >
                      {tier.tierName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: isLocked ? '#94a3b8' : `${vis.labelColor}cc`, fontWeight: 500 }}
                    >
                      {tier.maxLifetimePoints
                        ? `${tier.minLifetimePoints.toLocaleString()} – ${tier.maxLifetimePoints.toLocaleString()} pts`
                        : `${tier.minLifetimePoints.toLocaleString()}+ pts`
                      }
                    </Typography>
                  </Box>
                </Box>

                {/* Multiplier badge */}
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  bgcolor: isLocked ? '#e2e8f0' : 'rgba(255,255,255,0.25)',
                  borderRadius: 2, px: 1.5, py: 0.5, mb: 1.5,
                }}>
                  <StarIcon sx={{ fontSize: 14, color: isLocked ? '#94a3b8' : (key === 'SILVER' ? '#f59e0b' : '#fff') }} />
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: isLocked ? '#94a3b8' : (key === 'SILVER' ? '#334155' : '#fff') }}
                  >
                    {tier.earningMultiplier}x points per $1
                  </Typography>
                </Box>

                {/* Progress bar (only if unlocked and has maxLifetimePoints) */}
                {isUnlocked && tier.maxLifetimePoints && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: isLocked ? '#94a3b8' : `${vis.labelColor}bb`, fontWeight: 500 }}>
                        {lifetimePoints <= tier.maxLifetimePoints
                          ? `${lifetimePoints.toLocaleString()} / ${tier.maxLifetimePoints.toLocaleString()} pts`
                          : 'Tier completed'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isLocked ? '#94a3b8' : `${vis.labelColor}bb`, fontWeight: 600 }}>
                        {progressInTier}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progressInTier}
                      sx={{
                        borderRadius: 4,
                        height: 6,
                        bgcolor: 'rgba(255,255,255,0.25)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: key === 'SILVER' ? '#475569' : 'rgba(255,255,255,0.9)',
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Locked: show how many pts needed */}
                {isLocked && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <LockIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                      Earn {ptsNeeded.toLocaleString()} more lifetime pts to unlock
                    </Typography>
                  </Box>
                )}

                {/* Top tier: show "Max tier" */}
                {isUnlocked && !tier.maxLifetimePoints && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <EmojiEventsIcon sx={{ fontSize: 14, color: key === 'SILVER' ? '#f59e0b' : 'rgba(255,255,255,0.8)' }} />
                    <Typography variant="caption" sx={{ color: key === 'SILVER' ? '#475569' : 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                      Highest tier — you've made it!
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

function Rewards() {
  const [confirmTier, setConfirmTier] = useState(null);
  const [redeemed, setRedeemed] = useState(null);
  const [error, setError] = useState(null);
  const { profile } = useContext(UserContext);

  const { data: loyaltyData, loading: loyaltyLoading, refetch } = useQuery(MY_LOYALTY, { fetchPolicy: 'network-only' });
  const { data: blackCardData } = useQuery(MY_BLACK_CARD, { fetchPolicy: 'cache-and-network', skip: !profile });
  const { program: blackCard, loaded: blackCardLoaded } = useBlackCardProgram();
  const { data: tiersData, loading: tiersLoading } = useQuery(REDEMPTION_TIERS);
  const { data: tierConfigData, loading: tierConfigLoading } = useQuery(TIER_CONFIG);
  const [redeemPoints, { loading: redeeming }] = useMutation(REDEEM, {
    onCompleted: (data) => {
      setRedeemed(data.redeemLoyaltyPoints);
      setConfirmTier(null);
      refetch();
    },
    onError: (err) => {
      setError(err.message);
      setConfirmTier(null);
    },
  });

  const loyalty = loyaltyData?.myLoyaltyPoints;
  const tiers = tiersData?.loyaltyRedemptionTiers ?? [];
  const tierConfigs = tierConfigData?.loyaltyTierConfig ?? [];

  if (loyaltyLoading || tiersLoading || tierConfigLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  // Balance card tier visuals
  const currentTierKey = loyalty?.tier || 'SILVER';
  const currentVis = TIER_VISUALS[currentTierKey] || TIER_VISUALS.SILVER;

  return (
    <Box sx={{ background: '#f8fafc', minHeight: '100vh', pb: 6 }}>
      <Header />
      <Container maxWidth="md" sx={{ pt: "80px" }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          My Rewards
        </Typography>

        {/* Black Card membership teaser / status card */}
        {(() => {
          const sub = blackCardData?.myBlackCardSubscription;
          const isActive = sub?.status === 'ACTIVE';
          const isCancelled = sub?.status === 'CANCELLED';
          // Turned off by the super admin: only members still see it.
          if (!sub && (!blackCardLoaded || !blackCard.enabled)) return null;
          const pitch = [
            blackCard.deliveryDiscountCap > 0 && `Up to ${bcMoney(blackCard.deliveryDiscountCap)} off delivery`,
            blackCard.bonusPointsPerOrder > 0 && `+${blackCard.bonusPointsPerOrder} pts/order`,
            `${bcMoney(blackCard.monthlyPrice)}/mo`,
          ].filter(Boolean).join(' · ');
          return (
            <Paper
              sx={{
                background: 'linear-gradient(135deg, #0a0a0a 0%, #1c1c1c 60%, #2a2a2a 100%)',
                borderRadius: 3,
                p: 3,
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #333',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="overline" sx={{ color: '#FFD700', letterSpacing: 2, fontWeight: 700, fontSize: 10 }}>
                  Premium Membership
                </Typography>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800 }}>
                  🖤 Black Card
                </Typography>
                {isActive ? (
                  <Typography variant="body2" sx={{ color: '#66bb6a', mt: 0.5 }}>
                    Active — renews {new Date(sub.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                ) : isCancelled ? (
                  <Typography variant="body2" sx={{ color: '#aaa', mt: 0.5 }}>
                    Cancelled — active until {new Date(sub.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: '#aaa', mt: 0.5 }}>
                    {pitch}
                  </Typography>
                )}
              </Box>
              <Button
                component={Link}
                to="/membership"
                variant="contained"
                size="small"
                sx={{
                  background: isActive ? 'transparent' : '#FFD700',
                  color: isActive ? '#FFD700' : '#000',
                  border: isActive ? '1px solid #FFD700' : 'none',
                  fontWeight: 700,
                  '&:hover': { background: isActive ? 'rgba(255,215,0,0.1)' : '#e6c200' },
                }}
              >
                {isActive ? 'Manage' : 'Learn More'}
              </Button>
            </Paper>
          );
        })()}


        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {redeemed && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRedeemed(null)}>
            Coupon <strong>{redeemed.code}</strong> (${parseFloat(redeemed.discountValue).toFixed(2)} off) added to your wallet!
            Expires {new Date(redeemed.expiresAt).toLocaleDateString()}.
            <Link to="/my-coupons" style={{ marginLeft: 8 }}>View Wallet →</Link>
          </Alert>
        )}

        {/* Balance card */}
        <Paper
          sx={{
            p: 3, mb: 3,
            background: currentVis.gradient,
            boxShadow: currentVis.glow,
            borderRadius: 3,
            color: currentTierKey === 'SILVER' ? '#1e293b' : '#fff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28,
              }}>
                {currentVis.icon}
              </Box>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>Points Balance</Typography>
                <Typography variant="h3" fontWeight={800} lineHeight={1}>{(loyalty?.balance ?? 0).toLocaleString()}</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Lifetime Points</Typography>
              <Typography variant="h5" fontWeight={700}>{(loyalty?.lifetimePoints ?? 0).toLocaleString()}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Tier</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={currentTierKey}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    color: currentTierKey === 'SILVER' ? '#334155' : '#fff',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    border: '1px solid rgba(255,255,255,0.4)',
                  }}
                />
              </Box>
            </Box>

            {loyalty?.nextTier && (
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {loyalty.pointsToNextTier?.toLocaleString()} more pts to {loyalty.nextTier}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.round(((loyalty.lifetimePoints - (loyalty.lifetimePoints - loyalty.pointsToNextTier)) / Math.max(1, loyalty.pointsToNextTier)) * 100))}
                  sx={{
                    mt: 0.5, borderRadius: 4, height: 8,
                    bgcolor: 'rgba(255,255,255,0.25)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: currentTierKey === 'SILVER' ? '#475569' : 'rgba(255,255,255,0.9)',
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            )}

            <Button
              component={Link} to="/my-coupons"
              variant="outlined" startIcon={<CardGiftcardIcon />} size="small"
              sx={{
                borderColor: currentTierKey === 'SILVER' ? '#475569' : 'rgba(255,255,255,0.6)',
                color: currentTierKey === 'SILVER' ? '#334155' : '#fff',
                '&:hover': { borderColor: currentTierKey === 'SILVER' ? '#334155' : '#fff', bgcolor: 'rgba(255,255,255,0.15)' },
              }}
            >
              My Coupons
            </Button>
          </Box>
        </Paper>

        {/* Tier system explanation banner */}
        <TierBanner loyalty={loyalty} tierConfigs={tierConfigs} />

        {/* Redemption tiers */}
        <Typography variant="h6" fontWeight={600} gutterBottom>Redeem Points for Coupons</Typography>
        <Paper sx={{ mb: 3, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
              <TableRow>
                <TableCell>Points Required</TableCell>
                <TableCell>You Get</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tiers.map(tier => {
                const canAfford = (loyalty?.balance ?? 0) >= tier.pointsRequired;
                const shortfall = tier.pointsRequired - (loyalty?.balance ?? 0);
                return (
                  <TableRow key={tier._id} sx={{ opacity: canAfford ? 1 : 0.5 }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StarIcon sx={{ color: '#f59e0b', fontSize: 16 }} />
                        <Typography fontWeight={600}>{tier.pointsRequired.toLocaleString()} pts</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700} color="success.main">
                        ${parseFloat(tier.couponValue).toFixed(2)} off
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {canAfford ? (
                        <Button variant="contained" size="small" onClick={() => setConfirmTier(tier)}>
                          Redeem
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Need {shortfall.toLocaleString()} more pts
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {tiers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">No redemption tiers configured yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        {/* Transaction history */}
        <Typography variant="h6" fontWeight={600} gutterBottom>Points History</Typography>
        <Paper sx={{ overflow: 'hidden' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Points</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(loyalty?.transactions ?? []).map(tx => (
                <TableRow key={tx._id}>
                  <TableCell>{tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>{tx.type.replace(/_/g, ' ')}</TableCell>
                  <TableCell>
                    <Typography fontWeight={700} sx={{ color: TX_COLORS[tx.type] || '#333' }}>
                      {tx.points > 0 ? '+' : ''}{tx.points}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={tx.status} size="small"
                      sx={{ bgcolor: tx.status === 'PENDING' ? '#fef9c3' : tx.status === 'CONFIRMED' ? '#dcfce7' : '#f1f5f9',
                            color: tx.status === 'PENDING' ? '#ca8a04' : tx.status === 'CONFIRMED' ? '#16a34a' : '#64748b' }} />
                  </TableCell>
                  <TableCell>{tx.description || '—'}</TableCell>
                </TableRow>
              ))}
              {(loyalty?.transactions ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">No transactions yet — place an order to start earning!</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Container>

      {/* Confirm redemption dialog */}
      <RmfDialog open={!!confirmTier} onClose={() => !redeeming && setConfirmTier(null)}>
        <DialogIcon tone="gold" sx={{ mb: 2.5 }}>
          <RedeemRoundedIcon />
        </DialogIcon>
        <RmfDialogTitle besideClose={false}>Confirm Redemption</RmfDialogTitle>
        <DialogText>
          Spend <strong>{confirmTier?.pointsRequired?.toLocaleString()} points</strong> for a{' '}
          <strong>${parseFloat(confirmTier?.couponValue ?? 0).toFixed(2)} off</strong> coupon?
        </DialogText>
        <DialogText sx={{ fontSize: 13, mt: 1 }}>Points cannot be returned after redemption.</DialogText>
        <RmfDialogActions>
          <Box
            component="button"
            type="button"
            disabled={redeeming}
            aria-busy={redeeming}
            onClick={() => redeemPoints({ variables: { tierId: confirmTier._id } })}
            sx={dialogButtonSx.primary}
          >
            {redeeming ? 'Redeeming...' : 'Confirm'}
          </Box>
          <Box component="button" type="button" disabled={redeeming} onClick={() => setConfirmTier(null)} sx={dialogButtonSx.secondary}>
            Cancel
          </Box>
        </RmfDialogActions>
      </RmfDialog>
    </Box>
  );
}

export default Rewards;
